import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./MarketingDashboard.css";

ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const API_BASE = "https://cleezoclass.com:8443/api";

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.result)) return value.result;
  return value ? [value] : [];
};

const emptyCounts = {
  leads: 0,
  demos: 0,
  mous: 0,
  centralizations: 0,
};

const MarketingDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedExecutiveId, setSelectedExecutiveId] = useState("all");
  const [counts, setCounts] = useState(emptyCounts);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [empName, setEmpName] = useState("");
  const [designation, setDesignation] = useState("");
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  useEffect(() => {
    const storedEmpName = localStorage.getItem("empName");
    const storedDesignation = localStorage.getItem("designation");
    const storedEmpId = localStorage.getItem("empId");

    if (!storedEmpId || !storedEmpName || !storedDesignation) {
      alert("Session expired. Please log in again.");
      window.location.href = "/";
      return;
    }

    setEmpName(storedEmpName);
    setDesignation(storedDesignation);
    setIsLoadingSession(false);
  }, []);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const employeesRes = await axios.get(`${API_BASE}/employee-names`);
        const marketingEmployees = ensureArray(employeesRes.data).filter(
          (employee) => employee.designation === "Marketing Executive"
        );
        setEmployees(marketingEmployees);
      } catch (error) {
        console.error("[MarketingDashboard] error loading employees:", error);
        setEmployees([]);
      }
    };

    loadEmployees();
  }, []);

  useEffect(() => {
    const loadCounts = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const params = selectedExecutiveId === "all" ? {} : { empId: selectedExecutiveId };
        const response = await axios.get(`${API_BASE}/marketing-dashboard-counts`, { params });

        setCounts(response.data?.counts || emptyCounts);
      } catch (error) {
        console.error("[MarketingDashboard] error loading counts:", error);
        setErrorMessage("Unable to load marketing dashboard data.");
        setCounts(emptyCounts);
      } finally {
        setLoading(false);
      }
    };

    loadCounts();
  }, [selectedExecutiveId]);

  const marketingExecutives = useMemo(() => {
    return employees.filter(
      (employee, index, self) =>
        index ===
        self.findIndex((entry) => String(entry.empId) === String(employee.empId))
    );
  }, [employees]);

  const selectedExecutiveName =
    selectedExecutiveId === "all"
      ? "All Marketing Executives"
      : marketingExecutives.find((item) => String(item.empId) === String(selectedExecutiveId))
          ?.empName || "Selected Executive";

  const chartData = useMemo(
    () => [
      { name: "Leads", value: counts.leads, fill: "#2563eb" },
      { name: "Demos", value: counts.demos, fill: "#7c3aed" },
      { name: "MOU's", value: counts.mous, fill: "#ef4444" },
      { name: "Centralizations", value: counts.centralizations, fill: "#475569" },
    ],
    [counts]
  );

  if (isLoadingSession) {
    return (
      <div className="marketing-dashboard mainBlock">
        <div className="marketing-dashboard-loading-screen">Loading session...</div>
      </div>
    );
  }

  return (
    <div className="marketing-dashboard mainBlock">
      <div className="marketing-dashboard-header">
        <h2>
          {designation === "Marketing Manager" ? "Marketing Dashboard" : "Sales Dashboard"}
        </h2>
      </div>

      <div className="marketing-dashboard-section-box">
        <div className="marketing-dashboard-summary-row">
          <div className="marketing-summary-card">
            <span>Total Executives</span>
            <strong>{marketingExecutives.length}</strong>
          </div>
          <div className="marketing-summary-card">
            <span>Manager</span>
            <strong>{empName}</strong>
          </div>
          <div className="marketing-summary-card">
            <span>Executive Name</span>
            <strong>{selectedExecutiveName}</strong>
          </div>
        </div>

        <div className="marketing-dashboard-chart-block">
          <div className="marketing-dashboard-chart-header marketing-dashboard-chart-header-row">
            <div>
              <h3>{selectedExecutiveName}</h3>
              <p>Leads, demos, MOU's and centralizations based on the selected employee.</p>
            </div>
            <label className="marketing-dashboard-select marketing-dashboard-select-inline">
              <span>Marketing Executive</span>
              <select
                value={selectedExecutiveId}
                onChange={(e) => setSelectedExecutiveId(e.target.value)}
                disabled={loading || marketingExecutives.length === 0}
              >
                <option value="all"> Select all </option>
                {marketingExecutives.map((employee) => (
                  <option key={employee.empId} value={employee.empId}>
                    {employee.empName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="marketing-dashboard-chart-container">
            {loading ? (
              <div className="marketing-dashboard-loading">Loading data...</div>
            ) : errorMessage ? (
              <div className="marketing-dashboard-error">{errorMessage}</div>
            ) : (
              <Bar
                data={{
                  labels: chartData.map((item) => item.name),
                  datasets: [
                    {
                      label: "Count",
                      data: chartData.map((item) => item.value),
                      backgroundColor: chartData.map((item) => item.fill),
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (context) => ` ${context.parsed.y} count`,
                      },
                    },
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: { color: "#334155", font: { size: 13, weight: "600" } },
                    },
                    y: {
                      beginAtZero: true,
                      ticks: { color: "#64748b", precision: 0 },
                      grid: { color: "rgba(148, 163, 184, 0.18)" },
                    },
                  },
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingDashboard;
